import styled, { useTheme } from 'styled-components'
import { memo, startTransition, Suspense, useCallback, useMemo, useRef, useState } from 'react'
import { useStore, useSelector, useDispatch } from 'react-redux'
import { Column } from 'react-virtualized/dist/es/Table'
import { VirtualizedTable } from './VirtualizedTable'
import { Shimmer, ShimmerElementType } from '@fluentui/react/lib/Shimmer'
import { itemHelper, stateHelper } from '../../common/src/helpers'
import { IconCell } from '../icon/IconCell'
import { UserCollectionCell } from '../users/usersCollection/UserCollectionCell.js'
import { ITEMS_SORT_TYPE, setItemsSortAscending, setItemsSortType } from '../../common/src/actions/filtersActions'
import { API_ACCEPTED_SORT_KEYS, getGenericSortPredicateForItems } from '../../common/src/helpers/itemsFiltersHelper'
import { SortDirection } from 'react-virtualized/dist/commonjs/Table'
import { Loading } from '../basic/Loading'
import { useNarrowWidth } from '../../hooks/useNarrowWidth'
import { extendedTableRowRenderer } from './ExtendedTableRowRenderer'
import { SPECIAL_TABLE_CELL } from './index'
import { OfficeLikeQuadrantSelector as QuadrantSelector } from '../input/quadrant/OfficeLikeQuadrantSelector'
import { useTranslation } from 'react-i18next'
import { ItemContextualMenu } from '../item/ItemContextualMenu'
import { LazyIViewPlaceholder } from '../placeholder/LazyIViewPlaceholder'
import { useGetItemWhenOpenContextualMenu } from '../../hooks/itemContextualMenu'
import { useMobile } from '../../helpers/responsiveHelpers'
import { OwnerPanel } from '../item/OwnerPanel'
import { markItemAsModified, sendItem } from '../../common/src/actions/combinedAPI'
import { useUsersMenuProps } from '../../hooks/usersMenuPropsHooks'
import { useUserForEmail } from '../../common/src/hooks/usersHooks'
import { FlexColumn } from '../layout/FlexContainer'
import { getMultipleSelectedItems, isItemMultipleSelectionOn } from '../../selectors/uiSelectors'
import {
  clearItemMultipleSelection,
  setItemSelectedInMultipleSelection,
  setItemsSelectedInMultiSelection,
} from '../../actions/uiActions'
import {
  getItemsSortAscending,
  getItemsSortType,
  getItemsStarredFirst,
} from '../../common/src/selectors/filtersSelectors'
import { ITEM_FILTER_SECTION_KEY } from '../../common/src/reducers/filtersKeys'
import { AMPLITUDE_ACTION_TYPES, dispatchEvent } from '../../common/src/eventTracking/amplitudeEvents'
import { Map } from 'immutable'
import { Button, Checkbox, Popover, PopoverSurface, PopoverTrigger } from '@fluentui/react-components'
import { Tooltip } from '@/components/tooltip/Tooltip'
import { usePositioningMouseTarget } from '@fluentui/react-positioning'
import { formatDate } from '../../utils/datefns'
import { useChangeIcon } from '../../typedContexts'
import { cn } from '@/modules/classnames'
import {
  ArrowSortDownRegular,
  ArrowSortUpRegular,
  CheckmarkFilled,
  FlagRegular,
  GridRegular,
  PersonRegular,
  StarFilled,
} from '@fluentui/react-icons'
import { Info } from '@/components/BundledIcons'

const IMAGE_SIZE = 24

const IconContainer = styled.div`
  position: relative;
  width: ${IMAGE_SIZE}px;
  height: ${IMAGE_SIZE}px;
`

const SIcon = styled(IconCell).attrs({
  side: IMAGE_SIZE,
})`
  margin: auto;
`

const Star = () => <StarFilled className="absolute -left-0.5 -top-0.5 text-sm text-yellow-400" />

const Owner = ({ email, onClick }) => {
  const user = useUserForEmail(email)
  return (
    <UserCollectionCell
      className="mx-auto"
      onClick={onClick}
      personaProps={{ avatar: { size: IMAGE_SIZE } }}
      user={user}
      hidePersonaDetails
    />
  )
}

const Sort = ({ sortDirection }) => {
  const className = 'text-base ml-0.5'
  return sortDirection === SortDirection.DESC ? (
    <ArrowSortDownRegular className={className} />
  ) : (
    <ArrowSortUpRegular className={className} />
  )
}

const Text = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  color: ${p => p.theme.palette.black};
`

const ProjectName = styled.span`
  color: ${p => p.theme.palette.neutralPrimaryAlt};
`

const TextContainer = styled(FlexColumn)`
  align-items: flex-start;
`

const Percentage = styled.div`
  color: ${p => p.theme.palette.black};
`

const Line = styled(Shimmer).attrs({
  shimmerElements: [
    {
      type: ShimmerElementType.line,
      height: 12,
    },
  ],
})`
  flex: 1;
`

const headerStyle = {
  textAlign: 'center',
}

const columnStyle = {
  ...headerStyle,
}

const iconColumnStyle = {
  ...columnStyle,
  overflow: 'initial',
}

export const ITEM_COLUMN_KEYS = {
  SELECT: 'SELECT',
  QUADRANT: ITEMS_SORT_TYPE.QUADRANT,
  ICON: ITEMS_SORT_TYPE.ICON,
  OWNER: ITEMS_SORT_TYPE.OWNER_USERNAME,
  NAME: ITEMS_SORT_TYPE.NAME,
  PROGRESS: ITEMS_SORT_TYPE.COMPLETION,
  LAST_MODIFIED_TIMESTAMP: ITEMS_SORT_TYPE.LAST_MODIFIED_TIMESTAMP,
  DUE_DATE: ITEMS_SORT_TYPE.DUE_DATE,
  START_DATE: ITEMS_SORT_TYPE.START_DATE,
}

const addStyleForCompletedItem = (item, base = {}) => {
  const c = itemHelper.getCompletionPercentage(item)
  if (c >= 100) {
    base.textDecoration = 'line-through'
  }
  return base
}

const sortItems = (array, sortBy, sortDirection, starredFirst) => {
  const isAscending = sortDirection === SortDirection.ASC
  const predicate = getGenericSortPredicateForItems(sortBy, isAscending, starredFirst)
  return [...array].sort(predicate)
}

const defaultCellDataGetter = ({ rowData }) => rowData

const baseColumnRender = customRender => props => {
  const { rowData } = props
  if (!rowData) {
    return <Line />
  }
  if (rowData?.meta === SPECIAL_TABLE_CELL) {
    return
  }
  return customRender(props)
}

const OwnerPanelComponent = ({ item, isOpenOwnerView, hideOwnerView }) => {
  const username = itemHelper.getOwnerUsername(item)
  const owner = useUserForEmail(username)

  const projectID = itemHelper.getProjectIdd(item)
  const project = useSelector(state => stateHelper.getProject(state, projectID))
  const menuProps = useUsersMenuProps({ users: owner ? [owner] : null, object: item })
  const dispatch = useDispatch()

  const changeOwnerProperty = useCallback(
    username => {
      const finalItem = item.set(itemHelper.KEYS.OWNER_USERNAME, username)
      startTransition(() => void dispatch(markItemAsModified(finalItem)))
      dispatch(sendItem(finalItem))
    },
    [item, dispatch]
  )

  return (
    <OwnerPanel
      user={owner}
      project={project}
      onChange={changeOwnerProperty}
      isOpen={isOpenOwnerView}
      onDismiss={hideOwnerView}
      menuProps={menuProps}
    />
  )
}

const StarredHeader = content => rowProps => (
  <div {...rowProps} className={cn(rowProps.className, 'cursor-auto pl-5 pr-2 hover:bg-transparent')}>
    {content}
  </div>
)

export const ItemVirtualizedTable = memo(
  ({
    items,
    selectedItem,
    isSortEnabled = false,
    loading = false,
    placeholder,
    displayProject = false,
    showQuadrant = false,
    filterMode = ITEM_FILTER_SECTION_KEY.PROJECT_LIST,
    defaultSortBy,
    queryingSearchAPI = false,
    showOwner = true,
    ...rest
  }) => {
    const { t } = useTranslation()
    const sortBy = useSelector(state => getItemsSortType(state, filterMode))
    const sortDirection = useSelector(state => getItemsSortAscending(state, filterMode))
      ? SortDirection.ASC
      : SortDirection.DESC
    const starredFirst = useSelector(state => getItemsStarredFirst(state, filterMode))

    const store = useStore()
    const _rows = useMemo(() => {
      // If searching through API and sortBy is accepted by API, then items are already sorted
      if (queryingSearchAPI && API_ACCEPTED_SORT_KEYS.has(sortBy)) {
        return items
      }
      if (isSortEnabled && sortBy) {
        return sortItems(items, sortBy, sortDirection, starredFirst)
      }
      return items
    }, [queryingSearchAPI, sortBy, isSortEnabled, items, sortDirection, starredFirst])
    const rows = useMemo(() => {
      if (!starredFirst) return _rows
      const starredItems = _rows.filter(itemHelper.isStarred)
      const notStarredItems = _rows.filter(item => !itemHelper.isStarred(item))
      const showHeaders = starredItems.length > 0 && notStarredItems.length > 0
      return [
        ...(showHeaders
          ? [
              {
                meta: SPECIAL_TABLE_CELL,
                render: StarredHeader(
                  <>
                    <StarFilled className="text-base text-yellow-400" />
                    <span className="ml-2 font-bold">{t('item.starred_items')}</span>
                    <Popover>
                      <PopoverTrigger disableButtonEnhancement>
                        <Button
                          className="!ml-1"
                          icon={<Info className="text-base" />}
                          appearance="subtle"
                          size="small"
                        />
                      </PopoverTrigger>
                      <PopoverSurface>{t('item.starred_items_info')}</PopoverSurface>
                    </Popover>
                  </>
                ),
              },
            ]
          : []),
        ...starredItems,
        ...(showHeaders
          ? [
              {
                meta: SPECIAL_TABLE_CELL,
                render: StarredHeader(<span className="font-bold">{t('item.rest_items')}</span>),
              },
            ]
          : []),
        ...notStarredItems,
      ]
    }, [_rows, starredFirst, t])
    const narrow = useNarrowWidth()
    const stateRef = useRef()
    stateRef.current = useStore().getState()
    const [showContextualMenu, setShowContextualMenu] = useState(false)
    const [contextualMenuTarget, setContextualMenuTarget] = usePositioningMouseTarget()
    const [contextualMenuItem, setContextualMenuItem] = useState(undefined)
    const mobilePlatform = useMobile()
    const [isOpenOwnerView, setOpenOwnerView] = useState(false)
    const dispatch = useDispatch()
    const [actionItem, setActionItem] = useState(undefined)
    const multipleSelection = useSelector(isItemMultipleSelectionOn)
    const multipleSelectedItemIds = useSelector(getMultipleSelectedItems)
    const theme = useTheme()

    const handleSortChange = useCallback(
      ({ sortBy, sortDirection }) => {
        dispatch(setItemsSortType(sortBy, filterMode))
        dispatch(setItemsSortAscending(sortDirection === 'ASC', filterMode))
      },
      [dispatch, filterMode]
    )

    useGetItemWhenOpenContextualMenu(contextualMenuItem, showContextualMenu)

    const showOwnerView = useCallback(
      (event, item) => {
        if (!mobilePlatform) {
          event.preventDefault()
          event.stopPropagation()
        }
        setActionItem(item)
        setOpenOwnerView(true)
      },
      [setOpenOwnerView, setActionItem, mobilePlatform]
    )

    const hideOwnerView = useCallback(() => {
      setOpenOwnerView(false)
    }, [setOpenOwnerView])

    const changeIconProperty = useCallback(
      (item, iconName) => {
        const finalItem = item.set(itemHelper.KEYS.ICON, iconName)
        startTransition(() => void dispatch(markItemAsModified(finalItem)))
        dispatch(sendItem(finalItem))
      },
      [dispatch]
    )

    const { openModal: openChangeIconModal } = useChangeIcon()

    const onClickIcon = useCallback(
      item => {
        const onChangeIcon = iconName => {
          changeIconProperty(item, iconName)
        }
        setActionItem(item)
        openChangeIconModal({ currentIcon: itemHelper.getIconNameSafelyWithoutExtension(item), onChangeIcon })
      },
      [changeIconProperty, openChangeIconModal]
    )

    const handleSelectAllItems = useCallback(() => {
      const ids = Map.isMap(items) ? items.keys() : items.map(item => itemHelper.getId(item))
      dispatch(setItemsSelectedInMultiSelection(ids))
    }, [dispatch, items])

    const headerRenderer = useCallback(
      ({ dataKey, sortBy, sortDirection, label }) => {
        let main
        switch (dataKey) {
          case ITEM_COLUMN_KEYS.SELECT: {
            const allChecked =
              (items.size ?? items.length) > 0 &&
              items.every(item => multipleSelectedItemIds.has(itemHelper.getId(item)))
            main = (
              <Tooltip
                relationship="label"
                content={t(`item.multiple_selection.${allChecked ? 'clear_selection' : 'select_all'}`)}
              >
                <Checkbox
                  size={narrow ? 'large' : 'medium'}
                  checked={allChecked ? true : multipleSelectedItemIds.size > 0 ? 'mixed' : false}
                  onChange={allChecked ? () => dispatch(clearItemMultipleSelection()) : handleSelectAllItems}
                />
              </Tooltip>
            )
            break
          }
          case ITEM_COLUMN_KEYS.ICON:
            main = (
              <div className="flex w-full items-center justify-center">
                <FlagRegular className="text-xl" />
              </div>
            )
            break
          case ITEM_COLUMN_KEYS.QUADRANT:
            main = (
              <div className="flex w-full items-center justify-center">
                <GridRegular className="text-xl" />
              </div>
            )
            break
          case ITEM_COLUMN_KEYS.OWNER:
            main = (
              <div className="flex w-full items-center justify-center">
                <PersonRegular className="text-xl" />
              </div>
            )
            break
          default:
            main = label
            break
        }
        return (
          <div className="flex items-center font-semibold normal-case text-pm-black">
            {main}
            {isSortEnabled && sortBy === dataKey && <Sort sortDirection={sortDirection} />}
          </div>
        )
      },
      [dispatch, handleSelectAllItems, isSortEnabled, items, multipleSelectedItemIds, narrow, t]
    )

    const iconRenderer = useMemo(
      () =>
        baseColumnRender(({ rowData }) => {
          const iconName = itemHelper.getIconNameSafelyWithoutExtension(rowData)
          const isStarred = itemHelper.isStarred(rowData)

          return (
            <IconContainer>
              {iconName && <SIcon icon={iconName} onClick={() => onClickIcon(rowData)} />}
              {isStarred && <Star />}
            </IconContainer>
          )
        }),
      [onClickIcon]
    )

    const quadrantRenderer = useMemo(
      () =>
        baseColumnRender(({ rowData }) => {
          const q = itemHelper.getQuadrant(rowData)
          const pid = itemHelper.getProjectIdd(rowData)
          const project = stateHelper.getProject(store.getState(), pid)
          return project ? <QuadrantSelector size={IMAGE_SIZE} selected={q} project={project} /> : null
        }),
      [store]
    )

    const ownerRenderer = useMemo(
      () =>
        baseColumnRender(({ rowData }) => {
          const email = itemHelper.getOwnerUsername(rowData)
          return <Owner email={email} onClick={event => showOwnerView(event, rowData)} />
        }),
      [showOwnerView]
    )

    const nameRenderer = useMemo(
      () =>
        baseColumnRender(({ rowData }) => {
          const name = itemHelper.getName(rowData)
          const style = addStyleForCompletedItem(rowData)
          const projectName = displayProject && stateHelper.getHumanizedProjectNameForItem(stateRef.current, rowData)
          return (
            <TextContainer>
              <Text style={style} title={name}>
                {name}
              </Text>
              {displayProject && <ProjectName style={{ fontSize: 10 }}>{projectName}</ProjectName>}
            </TextContainer>
          )
        }),
      [stateRef, displayProject]
    )

    const progressRenderer = useMemo(
      () =>
        baseColumnRender(({ rowData }) => {
          const c = itemHelper.getCompletionPercentage(rowData)
          if (c >= 100) {
            return <CheckmarkFilled />
          } else if (c <= 0) {
            return false
          }
          return <Percentage>{c + '%'}</Percentage>
        }),
      []
    )

    const lastModifiedDateGetter = useMemo(
      () =>
        baseColumnRender(({ rowData }) => {
          return itemHelper.getLastModifiedTimestampDate(rowData)
        }),
      []
    )

    const dueDateGetter = useMemo(
      () =>
        baseColumnRender(({ rowData }) => {
          return itemHelper.getDueDate(rowData)
        }),
      []
    )

    const startDateGetter = useMemo(
      () =>
        baseColumnRender(({ rowData }) => {
          return itemHelper.getStartDate(rowData)
        }),
      []
    )

    const dateRenderer = useMemo(
      () =>
        baseColumnRender(({ dataKey, cellData, rowData }) => {
          if (!cellData) {
            return false
          }
          const text = formatDate('ShortenedAllDay')(cellData)
          const style = addStyleForCompletedItem(rowData)

          if (dataKey === ITEM_COLUMN_KEYS.DUE_DATE) {
            const dueColor = itemHelper.getDueColor(rowData, theme.palette.affirmative, theme.palette.negative)
            if (dueColor) {
              style.color = dueColor
            }
          }
          return <Text style={style}>{text}</Text>
        }),
      [theme.palette]
    )

    const selectRenderer = useMemo(
      () =>
        baseColumnRender(({ rowData }) => {
          const itemId = itemHelper.getId(rowData)
          const selected = multipleSelectedItemIds.has(itemId)
          return (
            <Tooltip content={selected ? t('general.unselect') : t('general.select')} relationship="label">
              <Checkbox
                size={narrow ? 'large' : 'medium'}
                checked={selected}
                onChange={(ev, data) => {
                  dispatch(setItemSelectedInMultipleSelection(itemId, data.checked))
                }}
                onClick={ev => ev.stopPropagation()}
              />
            </Tooltip>
          )
        }),
      [dispatch, multipleSelectedItemIds, narrow, t]
    )

    const noRowsRenderer = useCallback(() => {
      return (
        <Suspense fallback={<Loading />}>
          <LazyIViewPlaceholder {...placeholder} />
        </Suspense>
      )
    }, [placeholder])

    const handleShowContextualMenu = ({ event, rowData }) => {
      if (rowData === undefined) return
      event.preventDefault()
      setContextualMenuTarget(event)
      setContextualMenuItem(rowData)
      setShowContextualMenu(true)
      dispatch(dispatchEvent(AMPLITUDE_ACTION_TYPES.ITEM_CONTEXTUAL_MENU_OPEN))
    }

    return (
      <>
        <VirtualizedTable
          rows={rows}
          selectedRow={selectedItem}
          noRowsRenderer={noRowsRenderer}
          sortBy={sortBy}
          sortDirection={sortDirection}
          sort={handleSortChange}
          loading={loading}
          rowRenderer={extendedTableRowRenderer}
          onRowRightClick={handleShowContextualMenu}
          {...rest}
        >
          {multipleSelection && (
            <Column
              dataKey={ITEM_COLUMN_KEYS.SELECT}
              headerRenderer={headerRenderer}
              cellDataGetter={defaultCellDataGetter}
              cellRenderer={selectRenderer}
              width={narrow ? 38 : 30}
              disableSort
              headerStyle={headerStyle}
              onClick={ev => ev.stopPropagation()}
            />
          )}
          {!narrow && showQuadrant && (
            <Column
              label={t('item.quadrant')}
              dataKey={ITEM_COLUMN_KEYS.QUADRANT}
              headerRenderer={headerRenderer}
              cellDataGetter={defaultCellDataGetter}
              cellRenderer={quadrantRenderer}
              width={30}
              disableSort={!isSortEnabled}
              headerStyle={headerStyle}
            />
          )}
          {!narrow && (
            <Column
              label={t('item.icon.title')}
              dataKey={ITEM_COLUMN_KEYS.ICON}
              headerRenderer={headerRenderer}
              cellDataGetter={defaultCellDataGetter}
              cellRenderer={iconRenderer}
              width={30}
              disableSort={!isSortEnabled}
              headerStyle={headerStyle}
              style={iconColumnStyle}
            />
          )}
          {showOwner && (
            <Column
              label={t('item.owner')}
              dataKey={ITEM_COLUMN_KEYS.OWNER}
              headerRenderer={headerRenderer}
              cellDataGetter={defaultCellDataGetter}
              cellRenderer={ownerRenderer}
              width={40}
              disableSort={!isSortEnabled}
              headerStyle={headerStyle}
              style={columnStyle}
            />
          )}
          <Column
            label={t('item.name')}
            dataKey={ITEM_COLUMN_KEYS.NAME}
            headerRenderer={headerRenderer}
            cellDataGetter={defaultCellDataGetter}
            cellRenderer={nameRenderer}
            flexGrow={1}
            width={200}
            disableSort={!isSortEnabled}
          />
          {!narrow && (
            <Column
              label={t('item.percent')}
              dataKey={ITEM_COLUMN_KEYS.PROGRESS}
              headerRenderer={headerRenderer}
              cellDataGetter={defaultCellDataGetter}
              cellRenderer={progressRenderer}
              width={40}
              disableSort={!isSortEnabled}
              headerStyle={headerStyle}
              style={columnStyle}
            />
          )}
          {!narrow && (
            <Column
              label={t('item.modified')}
              dataKey={ITEM_COLUMN_KEYS.LAST_MODIFIED_TIMESTAMP}
              headerRenderer={headerRenderer}
              cellDataGetter={lastModifiedDateGetter}
              cellRenderer={dateRenderer}
              width={84}
              disableSort={!isSortEnabled}
              headerStyle={headerStyle}
              style={columnStyle}
            />
          )}
          {sortBy === ITEM_COLUMN_KEYS.START_DATE ? (
            <Column
              label={t('item.start')}
              dataKey={ITEM_COLUMN_KEYS.START_DATE}
              headerRenderer={headerRenderer}
              cellDataGetter={startDateGetter}
              cellRenderer={dateRenderer}
              disableSort={!isSortEnabled}
              width={84}
              headerStyle={headerStyle}
              style={columnStyle}
            />
          ) : (
            <Column
              label={t('item.due')}
              dataKey={ITEM_COLUMN_KEYS.DUE_DATE}
              headerRenderer={headerRenderer}
              cellDataGetter={dueDateGetter}
              cellRenderer={dateRenderer}
              disableSort={!isSortEnabled}
              width={84}
              headerStyle={headerStyle}
              style={columnStyle}
            />
          )}
        </VirtualizedTable>
        {showContextualMenu && (
          <ItemContextualMenu
            itemId={itemHelper.getId(contextualMenuItem)}
            target={contextualMenuTarget}
            onOpenChange={(ev, data) => setShowContextualMenu(data.open)}
            allItemsInList={items}
          />
        )}
        {!mobilePlatform && (
          <OwnerPanelComponent item={actionItem} isOpenOwnerView={isOpenOwnerView} hideOwnerView={hideOwnerView} />
        )}
      </>
    )
  }
)
